"use client";
import constate from "constate";
import { useEffect, useState } from "react";
import { getUser } from "@/app/(hub)/settings/user/services";
import { User } from "@/app/(hub)/settings/user/types";
import { getBusiness } from "@/app/(hub)/settings/business/services";
import { BusinessSchema } from "@/app/(hub)/settings/business/types";
import { Bank, BankAccountItem } from "@/app/(hub)/settings/payments/types";
import { getOutlets } from "@/app/(hub)/settings/outlets/services";
import { Outlets } from "@/app/(hub)/settings/outlets/types";
import { PaymentProvider } from "@/app/(procure)/procure/requests/types";
import { AppSettings } from "@/app/(hub)/settings/application/types";
import { getAppSettings } from "@/app/(hub)/settings/application/services";

function useStore() {
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState("dark");
  const [user, setUser] = useState<Partial<User>>({});
  const [outlet, setOutlet] = useState("");
  const [outlets, setOutlets] = useState<Outlets>([]);
  const [business, setBusiness] = useState<Partial<BusinessSchema>>({});
  const [allBanks, setAllBanks] = useState<Bank[]>([]);
  const [accounts, setAccounts] = useState<BankAccountItem[]>([]);
  const [appSettings, setAppSettings] = useState<Partial<AppSettings>>({});
  const [yuanToNaira, setYuanToNaira] = useState(appSettings?.yuanToNaira);
  const [paymentMethod, setPaymentMethod] = useState<PaymentProvider>(
    PaymentProvider.PAYSTACK,
  );
  const placeholder = "/images/default-placeholder.png";
  const isMaxSm =
    typeof window !== "undefined" ? window.innerWidth < 640 : false;

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      try {
        const [user, outlets, business, appSettings] = await Promise.all([
          getUser(),
          getOutlets(),
          getBusiness(),
          getAppSettings(),
        ]);
        const outletData = Array.isArray(outlets?.data) ? outlets.data : [];
        setUser(user);
        setOutlet(outletData[0]?._id ?? "");
        setOutlets(outletData);
        setBusiness(business);
        if ("ok" in appSettings && !appSettings.ok) {
          // Handle error case - set empty object or default values
          setAppSettings({});
          setYuanToNaira(0);
        } else {
          setAppSettings(appSettings as Partial<AppSettings>);
          setYuanToNaira(
            (appSettings as Partial<AppSettings>)?.yuanToNaira || 0,
          );
        }

        fetch("https://nigerianbanks.xyz/")
          .then((res) => res.json())
          .then((data) => setAllBanks(data));
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        setLoading(false);
      }
    }

    fetchAll();
  }, []);

  return {
    theme,
    user,
    outlet,
    outlets,
    loading,
    business,
    accounts,
    allBanks,
    setTheme,
    setUser,
    setOutlet,
    setOutlets,
    setBusiness,
    setAllBanks,
    setAccounts,
    placeholder,
    paymentMethod,
    setPaymentMethod,
    appSettings,
    yuanToNaira,
    isMaxSm,
  };
}

export const [StoreProvider, useStoreContext] = constate(useStore);
